// Pengaman upload mockup tanpa AI: target pesanan jelas, pratinjau, peringatan, konfirmasi + alasan.
import { useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import axios from "axios";
import { AlertTriangle, Info, Ban, RefreshCw, ShieldCheck } from "lucide-react";
import { canProceed, guardParams, guardErrorCode, guardErrorMessage, precheckErrorMessage, sha256Hex, REASON_MIN, orderNoCheckNotice } from "./upload_guard_logic";
import { notify } from "./modal_center";
import { toast } from "sonner";

const ONC_API = `${process.env.REACT_APP_BACKEND_URL}/api/order-no-check`;

export async function showOrderNoCheck(res) {
  const n = orderNoCheckNotice(res);
  if (!n) return;
  if (n.kind === "ok") { toast.success(n.message); return; }
  const ok = await notify.warning({ title: n.title, message: n.message, primaryLabel: "Sudah Diperiksa" });
  if (ok) axios.post(`${ONC_API}/checks/${n.id}/ack`, {}, { withCredentials: true }).catch(() => {});
}

export { withQuery } from "./upload_guard_logic";

const CH = { offline: "Offline", shopee: "Shopee", tokopedia: "Tokopedia" };
const SEV = {
  block: { bg: "#fee2e2", bd: "#ef4444", fg: "#991b1b", Icon: Ban },
  warn: { bg: "#fef3c7", bd: "#f59e0b", fg: "#92400e", Icon: AlertTriangle },
  info: { bg: "#e0f2fe", bd: "#38bdf8", fg: "#075985", Icon: Info },
};

export function usePrecheck(precheckUrl, file) {
  const [st, setSt] = useState({ status: "loading", check: null, error: "" });
  const run = useCallback(async () => {
    setSt({ status: "loading", check: null, error: "" });
    try {
      const sha256 = await sha256Hex(file);
      const r = await axios.post(precheckUrl, { sha256, filename: file.name || "" }, { withCredentials: true });
      setSt({ status: "ok", check: r.data, error: "" });
    } catch (e) {
      setSt({ status: "error", check: null, error: e?.isAxiosError || e?.response ? precheckErrorMessage(e) : (e?.message || precheckErrorMessage(e)) });
    }
  }, [precheckUrl, file]);
  useEffect(() => { run(); }, [run]);
  return { ...st, retry: run };
}

function TargetBanner({ t }) {
  return <div data-testid="upload-guard-target" style={{ border: "2px solid #0f172a", borderRadius: 8, padding: "8px 12px", background: "#f8fafc", color: "#0f172a" }}>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: "#475569" }}>
      Tujuan upload · {CH[t.channel] || t.channel}
    </div>
    <div data-testid="upload-guard-target-order" style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, wordBreak: "break-all" }}>{t.order_number}</div>
    <div data-testid="upload-guard-target-customer" style={{ fontSize: 15, fontWeight: 700 }}>{t.customer_name || "(nama pelanggan kosong)"}</div>
    {t.product_label && <div style={{ fontSize: 12, color: "#475569" }} data-testid="upload-guard-target-product">{t.product_label}</div>}
  </div>;
}

function WarningList({ warnings }) {
  if (!warnings.length) return <div data-testid="upload-guard-clear" style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: "#166534" }}>
    <ShieldCheck size={14} /> Tidak ada file identik di pesanan lain dan nama file tidak menyebut pesanan lain.
  </div>;
  return <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="upload-guard-warnings">
    {warnings.map((w, i) => {
      const s = SEV[w.severity] || SEV.warn;
      return <div key={i} data-testid={`upload-guard-warning-${w.code.toLowerCase()}`}
        style={{ display: "flex", gap: 8, padding: "8px 10px", background: s.bg, border: `1px solid ${s.bd}`, borderRadius: 6, color: s.fg, fontSize: 13 }}>
        <s.Icon size={16} style={{ flexShrink: 0, marginTop: 1 }} /><span>{w.message}</span>
      </div>;
    })}
  </div>;
}

export function GuardPanel({ pc, ack, setAck, reason, setReason, serverMsg }) {
  if (pc.status === "loading") return <div className="muted small" data-testid="upload-guard-loading">Memeriksa file…</div>;
  if (pc.status === "error") return <div data-testid="upload-guard-error" style={{ padding: "8px 10px", background: "#fee2e2", border: "1px solid #ef4444", borderRadius: 6, color: "#991b1b", fontSize: 13 }}>
    <div>{pc.error}</div>
    <button type="button" className="soft-btn small-btn" style={{ marginTop: 6 }} onClick={pc.retry} data-testid="upload-guard-retry">
      <RefreshCw size={13} /> Coba periksa lagi
    </button>
  </div>;
  const c = pc.check;
  return <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <TargetBanner t={c.target} />
    {serverMsg && <div className="warn-box small" data-testid="upload-guard-server-msg">{serverMsg}</div>}
    <WarningList warnings={c.warnings || []} />
    {c.needs_confirm && !c.blocked && <div data-testid="upload-guard-confirm-box" style={{ display: "flex", flexDirection: "column", gap: 6, padding: 8, border: "1px dashed #f59e0b", borderRadius: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 600 }}>Pilih file lain bila ini keliru. Lanjut hanya bila Anda yakin file ini benar untuk pesanan di atas.</div>
      <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
        <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} data-testid="upload-guard-ack" />
        Saya sudah memeriksa — file ini benar untuk <b>{c.target.order_number}</b>
      </label>
      <textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} maxLength={300}
        placeholder={`Alasan (wajib, min. ${REASON_MIN} karakter) — mis. desain sama untuk 2 pesanan pelanggan yang sama`}
        data-testid="upload-guard-reason" style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, width: "100%", resize: "vertical" }} />
    </div>}
    <div className="muted" style={{ fontSize: 11 }} data-testid="upload-guard-note">
      Pemeriksaan ini hanya mendeteksi file identik & nomor pesanan di nama file — bukan kecocokan isi desain. Tetap cek gambar secara visual.
    </div>
  </div>;
}

function FilePreview({ file }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!(file.type || "").startsWith("image/")) return undefined;
    const u = URL.createObjectURL(file); setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return <div style={{ textAlign: "center", background: "#f1f5f9", borderRadius: 6, padding: 8 }}>
    {url ? <img src={url} alt="pratinjau" data-testid="upload-guard-preview" style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain" }} />
      : <div className="muted small" data-testid="upload-guard-preview-file" style={{ padding: 16 }}>Pratinjau tidak tersedia (PDF/file).</div>}
    <div className="muted small" style={{ marginTop: 4, wordBreak: "break-all" }}>{file.name} · {Math.round((file.size || 0) / 1024)} KB</div>
  </div>;
}

export function UploadGuardModal({ file, precheckUrl, send, onDone, onCancel, onError }) {
  const pc = usePrecheck(precheckUrl, file);
  const [ack, setAck] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [serverMsg, setServerMsg] = useState("");
  const ok = pc.status === "ok" && canProceed(pc.check, ack, reason) && !busy;
  const submit = async () => {
    if (!ok) return;
    setBusy(true); setServerMsg("");
    try {
      const res = await send(guardParams(pc.check, ack, reason));
      onDone(res);
      showOrderNoCheck(res);
    }
    catch (e) {
      if (guardErrorCode(e)) { setServerMsg(guardErrorMessage(e)); setAck(false); await pc.retry(); }
      else onError(e);
    } finally { setBusy(false); }
  };
  return <div data-testid="upload-guard-modal" onClick={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
    <div className="card" style={{ maxWidth: 560, width: "100%", maxHeight: "92vh", overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Periksa sebelum upload mockup</h3>
      <FilePreview file={file} />
      <GuardPanel pc={pc} ack={ack} setAck={setAck} reason={reason} setReason={setReason} serverMsg={serverMsg} />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="soft-btn small-btn" onClick={onCancel} disabled={busy} data-testid="upload-guard-cancel">
          {pc.check?.warnings?.length ? "Pilih file lain" : "Batal"}
        </button>
        <button type="button" className="primary small-btn" onClick={submit} disabled={!ok} data-testid="upload-guard-submit">
          {busy ? "Mengunggah…" : pc.check?.needs_confirm ? "Konfirmasi & Unggah" : "Unggah"}
        </button>
      </div>
    </div>
  </div>;
}

let _open = false;
// Buka modal pengaman. Resolve hasil `send` bila terunggah, null bila dibatalkan; reject untuk error non-pengaman.
export function guardedUpload({ precheckUrl, file, send }) {
  if (_open) return Promise.resolve(null);
  _open = true;
  return new Promise((resolve, reject) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    const close = (fn, v) => { _open = false; root.unmount(); host.remove(); fn(v); };
    root.render(<UploadGuardModal file={file} precheckUrl={precheckUrl} send={send}
      onDone={v => close(resolve, v)} onCancel={() => close(resolve, null)} onError={e => close(reject, e)} />);
  });
}
